import { Component, input, OnInit, OnChanges, SimpleChanges, output, OnDestroy } from '@angular/core';
import { Tooltip } from 'primeng/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { IntegrationCardData } from '@/home/settings/integrations/integration-card/integration-card.types';
import { CardModule } from 'primeng/card';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { LayerService } from '@/core/services/open-api/layer.service';
import { TableModule } from 'primeng/table';
import { getMissingUnitsKey, TempStoreService } from '@/core/services/temp-store.service';
import {
    AvailableIntegrations,
    IntegrationConfig,
    isAvailableIntegration,
    UnitMapping,
} from '@/core/models/IntegrationEntity';
import { IntegrationUnitMappingComponent } from '@/home/settings/integrations/integration-details/integration-unit-mapping/integration-unit-mapping.component';
import { ToastService } from '@/core/services/toast.service';
import { IntegrationService } from '@/core/services/open-api/integration.service';
import { UserService } from '@/core/services/open-api/user.service';
import { CurrentUser } from '@/core/models/user';
import { AppCommonModule } from '@/common/app.common.module';
import { IntegrationApiKeyDialogComponent } from './integration-api-key-dialog/integration-api-key-dialog.component';
import { ConfirmationService } from 'primeng/api';
import { LoadingService } from '@/core/services/loading.service';
import { firstValueFrom, Subscription, filter } from 'rxjs';
import { IntegrationHelper } from '../integration-services/integration-helper';
import { Tag } from 'primeng/tag';
import { InitializationManager } from '@/core/utils/initialization-manager';

@Component({
    selector: 'app-integration-details',
    standalone: true,
    imports: [
        Tooltip,
        TranslatePipe,
        CardModule,
        FormsModule,
        ButtonModule,
        RouterLink,
        TableModule,
        IntegrationUnitMappingComponent,
        AppCommonModule,
        IntegrationApiKeyDialogComponent,
        Tag,
    ],
    templateUrl: './integration-details.component.html',
    styleUrls: ['./integration-details.component.scss'],
})
export class IntegrationDetailsComponent implements OnInit, OnChanges, OnDestroy {
    integration = input.required<IntegrationCardData>();
    disconnected = output<IntegrationConfig>();
    connected = output<IntegrationConfig>();
    i18nPrefix: string = '';
    layerOneId?: number;
    missingUnits: string[] | undefined = undefined;
    currentUser: CurrentUser | undefined = undefined;
    isPro: boolean = false;
    isAdmin: boolean = false;
    initialized: boolean = false;
    showAddApiKeyDialog = false;
    isApiKeyAuth: boolean = false;
    private subscriptions = new Subscription();
    private initMgr: InitializationManager;

    public constructor(
        private readonly layerService: LayerService,
        private readonly tempStore: TempStoreService,
        private readonly toastService: ToastService,
        private readonly integrationService: IntegrationService,
        private readonly userService: UserService,
        private readonly confirmationService: ConfirmationService,
        private readonly translate: TranslateService,
        private readonly loadingService: LoadingService,
        private route: ActivatedRoute,
        private readonly router: Router,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    public ngOnInit(): void {
        this.subscriptions.add(
            this.route.queryParams
                .pipe(filter((params) => params['oauthSuccess'] !== undefined))
                .subscribe((params) => {
                    const success = params['oauthSuccess'] === 'true';
                    if (success) {
                        IntegrationHelper.showConnectSuccessToast(
                            this.toastService,
                            this.translate,
                            this.integrationName,
                        );
                    } else {
                        IntegrationHelper.showConnectErrorToast(
                            this.toastService,
                            this.translate,
                            this.integrationName,
                        );
                    }

                    this.clearQueryParams('oauthSuccess', 'service', 'needsReauth');
                }),
        );

        this.subscriptions.add(
            this.route.queryParams.pipe(filter((params) => params['iid'] !== undefined)).subscribe((params) => {
                const iid: string = params['iid'];

                this.clearQueryParams('iid');

                this.integrationService.register(this.integration().serviceId, { iid }).subscribe({
                    next: () => {
                        IntegrationHelper.showConnectSuccessToast(
                            this.toastService,
                            this.translate,
                            this.integrationName,
                        );
                    },
                    error: (error: unknown) => {
                        console.error(`Error registering ${this.integration().serviceId} integration:`, error);
                        IntegrationHelper.showConnectErrorToast(
                            this.toastService,
                            this.translate,
                            this.integrationName,
                        );
                    },
                });
            }),
        );

        this.initMgr
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (user) => {
                    this.currentUser = user;
                    this.isPro = this.currentUser.isPro();
                    this.isAdmin = this.currentUser.isAdmin();
                },
            })
            .onInit(async () => {
                // Note: not as dependency because layer1id never changes and is in most cases cached, if not it is awaited.
                this.layerOneId = await this.layerService.getLayer1Id();
                const serviceId = this.integration().serviceId;
                if (isAvailableIntegration(serviceId)) {
                    this.missingUnits = this.tempStore.get<string[]>(getMissingUnitsKey(serviceId));
                } else {
                    this.missingUnits = undefined;
                }
                this.initialized = true;
            })
            .onChange(async () => {})
            .initialize();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['integration']) {
            const integration = changes['integration'].currentValue as IntegrationCardData;
            this.i18nPrefix = IntegrationHelper.getI18nPrefix(integration);
            const authMethod = integration.integration?.properties?.authorizationMethod;
            this.isApiKeyAuth = authMethod === 'apiKey' || authMethod === 'apiKeyWithEmail';
        }
    }

    public openInNewTab(url: string): void {
        window.open(url, '_blank', 'noopener');
    }

    public openDoc(): void {
        const isGerman = this.currentUser?.settings?.language === 'de';
        const serviceId = this.integration().serviceId;
        const url = isGerman
            ? `https://timebuzzer.com/de/help/integrationen/${serviceId}/`
            : `https://timebuzzer.com/help/integrations/${serviceId}/`;
        this.openInNewTab(url);
    }

    public onUnitsChanged(unitMapping: UnitMapping): void {
        this.removeOutdatedMissingUnits(unitMapping);
        this.updateIntegrationUnitMapping(unitMapping);
    }

    public updateIntegrationUnitMapping(unitMapping: Record<string, string>): void {
        const config = this.integrationConfig;
        if (!config?.configuration) {
            this.toastService.toast('UNEXPECTED_ERROR');
            return;
        }
        config.configuration.unitMapping = unitMapping;

        const patchConfig: IntegrationConfig = {
            service: config.service,
            configuration: {
                unitMapping: unitMapping,
            },
        };

        // TODO check what type of API feels better: service as part of config (like in update()) or as param?
        this.integrationService.patchConfig(config.service, patchConfig).subscribe({
            next: () => {
                this.toastService.successNoTranslate(
                    this.translate.instant('set_int_unit_change_success_toast_header'),
                    this.translate.instant('set_int_unit_change_success_toast_details', {
                        integrationName: this.integrationName,
                    }),
                );
            },
            error: () => {
                this.toastService.error();
            },
        });
    }

    /**
     * Removes units from the persisted missing-units list once they have been mapped.
     * This keeps the info message in the UI displayed and up-to-date.
     */
    private removeOutdatedMissingUnits(unitMapping: UnitMapping): void {
        if (this.missingUnits && unitMapping) {
            const definedUnits = Object.keys(unitMapping);
            const newMissingUnits = this.missingUnits.filter((unit) => !definedUnits.includes(unit));
            if (newMissingUnits.length === 0) {
                this.tempStore.delete(getMissingUnitsKey(this.integration().serviceId as AvailableIntegrations));
                this.missingUnits = undefined;
            } else {
                this.tempStore.set(
                    getMissingUnitsKey(this.integration().serviceId as AvailableIntegrations),
                    newMissingUnits,
                );
                this.missingUnits = newMissingUnits;
            }
        }
    }

    public connect(): void {
        const authMethod = this.integrationConfig.properties?.authorizationMethod;
        if (authMethod === undefined) {
            console.error('authMethod is undefined');
            return;
        }
        switch (authMethod) {
            case 'apiKey':
            case 'apiKeyWithEmail':
                this.showAddApiKeyDialog = true;
                break;
            case 'oauth2':
                void this.connectOAuth();
                break;
            case 'iid':
                this.connectIid();
                break;
            default:
                console.error('Unexpected authMethod: ' + authMethod);
        }
    }

    private connectIid(): void {
        const connectUrl = this.integration().extras?.connectUrl;
        if (connectUrl) {
            window.location.href = connectUrl;
        }
    }

    private async connectOAuth(): Promise<void> {
        this.loadingService.setLoading(true, this.constructor.name);
        try {
            const result = await firstValueFrom(this.integrationService.initOauth2(this.integrationConfig.service));
            window.location.href = result.url;
        } catch (error) {
            IntegrationHelper.showConnectErrorToast(this.toastService, this.translate, this.integrationName);
            console.error('Error initializing OAuth flow:', error);
        } finally {
            this.loadingService.setLoading(false, this.constructor.name);
        }
    }

    public reauth(): void {
        const authMethod = this.integrationConfig.properties?.authorizationMethod;
        switch (authMethod) {
            case 'apiKey':
            case 'apiKeyWithEmail':
                this.showAddApiKeyDialog = true;
                break;
            case 'oauth2':
                void this.connectOAuth();
                break;
            case 'iid':
                this.connectIid();
                break;
            default:
                console.error('Unexpected authMethod for re-auth operation: ' + authMethod);
        }
    }

    public disconnect(): void {
        this.confirmationService.confirm({
            message: this.translate.instant('set_int_disconnect_confirm', { integrationName: this.integrationName }),
            header: this.translate.instant('set_int_disconnect_confirm_header', {
                integrationName: this.integrationName,
            }),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('set_int_disconnect_accept_label'),
            rejectLabel: this.translate.instant('button_cancel'),
            rejectButtonStyleClass: 'p-button-text',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.deleteIntegration();
            },
        });
    }

    public onApiKeyChange(_apiKey: string): void {
        // Note: view is automatically updated by refetch and subscription of integrations.
    }

    private deleteIntegration(): void {
        this.integrationService.delete(this.integrationConfig.service).subscribe({
            next: () => {
                this.toastService.successNoTranslate(
                    this.translate.instant('set_int_disconnect_success_toast_header', {
                        integrationName: this.integrationName,
                    }),
                    this.translate.instant('set_int_disconnect_success_toast_detail'),
                );
                this.disconnected.emit(this.integrationConfig);
            },
            error: () => {
                this.toastService.errorNoTranslate(
                    this.translate.instant('standard_error'),
                    this.translate.instant('set_int_disconnect_error_toast_detail', {
                        integrationName: this.integrationName,
                    }),
                );
            },
        });
    }

    private clearQueryParams(...keys: string[]): void {
        const queryParams = Object.fromEntries(keys.map((k) => [k, null]));
        void this.router.navigate([], { queryParams, queryParamsHandling: 'merge', replaceUrl: true });
    }

    private get integrationConfig(): IntegrationConfig {
        const intConf = this.integration().integration;
        if (!intConf) {
            throw new Error('IntegrationDetailsComponent - Unexpected data: IntegrationConfig is missing');
        }
        return intConf;
    }

    private get integrationName(): string {
        return this.integration().name;
    }
}
